import React from 'react';
import { Grid, FormControl, InputLabel, Select, MenuItem, TextField, Typography, Autocomplete } from '@mui/material';
import { ZIM_PROVINCES, ZIM_DISTRICTS } from '../../constants/provinces';

/**
 * Structured Zimbabwe address: Province -> District (dependent) -> Suburb -> Street.
 * value:    { province, district, suburb, address }
 * onChange: (nextValue) => void   // receives the full merged object
 * show:     subset of ['province','district','suburb','address'] (default all)
 * required: marks province + district required
 * errors:   { province?, district? } optional error strings
 */
export default function ZimAddressFields({
  value = {},
  onChange,
  show = ['province', 'district', 'suburb', 'address'],
  required = false,
  errors = {},
}) {
  const { province = '', district = '', suburb = '', address = '' } = value;
  const districts = province ? (ZIM_DISTRICTS[province] || []) : [];
  const update = (patch) => onChange({ ...value, ...patch });

  return (
    <Grid container spacing={2}>
      {show.includes('province') && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required={required} error={!!errors.province}>
            <InputLabel>Province</InputLabel>
            <Select
              label="Province"
              value={province}
              onChange={(e) => update({ province: e.target.value, district: '' })}
            >
              {ZIM_PROVINCES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
            {errors.province && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{errors.province}</Typography>
            )}
          </FormControl>
        </Grid>
      )}

      {show.includes('district') && (
        <Grid item xs={12} sm={6}>
          {/* Free-text autocomplete: pick a suggested district/suburb or type your own
              (the curated lists never cover every Harare/Bulawayo suburb). */}
          <Autocomplete
            freeSolo
            options={districts}
            inputValue={district}
            disabled={!province}
            onChange={(e, val) => update({ district: val || '' })}
            onInputChange={(e, val, reason) => {
              if (reason === 'input' || reason === 'clear') update({ district: val });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="District / area"
                required={required}
                error={!!errors.district}
                helperText={errors.district || (province ? 'Pick your district/suburb or type your own' : 'Choose a province first')}
                placeholder="e.g. Borrowdale, Hillside, Mount Pleasant…"
              />
            )}
          />
        </Grid>
      )}

      {show.includes('suburb') && (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Suburb / area (optional)"
            placeholder="e.g. Glen Norah, Mucheke, Gwabalanda"
            value={suburb}
            onChange={(e) => update({ suburb: e.target.value })}
          />
        </Grid>
      )}

      {show.includes('address') && (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Street / address line (optional)"
            value={address}
            onChange={(e) => update({ address: e.target.value })}
          />
        </Grid>
      )}
    </Grid>
  );
}
